import { z } from 'zod';
import { BlockSchema } from './blocks';

/**
 * Playlists (PLATFORM_EXPANSION.md §2.3). A playlist is an ordered set of items:
 * a layout screen, a media asset, or a widget set — each with a duration and an
 * optional transition and an optional data-driven condition. Playlists publish
 * through the existing deployment versioning/snapshot/rollback rules (no second
 * mechanism) and are referenced by the schedule (X2.2).
 */

export const TRANSITIONS = ['none', 'fade', 'slide', 'kenburns', 'wipe'] as const;
export const TransitionSchema = z.enum(TRANSITIONS);
export type Transition = z.infer<typeof TransitionSchema>;

/** A data-driven condition: show this item only when the condition holds. */
export const PlaylistConditionSchema = z.object({
  data_key: z.string().min(1).max(120),
  op: z.enum(['gt', 'gte', 'lt', 'lte', 'eq', 'neq']),
  value: z.union([z.number(), z.string(), z.boolean()])
});
export type PlaylistCondition = z.infer<typeof PlaylistConditionSchema>;

const itemBase = {
  id: z.string().min(1),
  duration_s: z.number().int().min(1).max(86400),
  transition: TransitionSchema.default('fade'),
  /** Reference to a data key addressable via the connector/ingest model. */
  when: PlaylistConditionSchema.optional()
};

export const PlaylistItemSchema = z.discriminatedUnion('kind', [
  z.object({
    ...itemBase,
    kind: z.literal('layout'),
    layout_id: z.string().min(1),
    /** Optional: a multi-screen layout names which screen to show. */
    screen_id: z.string().min(1).optional()
  }),
  z.object({
    ...itemBase,
    kind: z.literal('media'),
    media_id: z.string().min(1),
    media_kind: z.enum(['image', 'video']),
    fit: z.enum(['cover', 'contain']).default('cover')
  }),
  z.object({
    ...itemBase,
    kind: z.literal('widget'),
    /** A widget set rendered on the screen grid; blocks are the same schema. */
    blocks: z.array(BlockSchema).min(1)
  })
]);
export type PlaylistItem = z.infer<typeof PlaylistItemSchema>;

export const PlaylistSchema = z.object({
  tenant: z.string().min(1),
  name: z.string().min(1).max(120),
  items: z.array(PlaylistItemSchema).min(1),
  /** Optional loop control; when absent the playlist loops indefinitely. */
  loop: z.boolean().default(true),
  status: z.enum(['draft', 'published']).default('draft')
});
export type Playlist = z.infer<typeof PlaylistSchema>;
